import React, { useEffect, useState, useMemo } from "react";
import {
  Box, Typography, TextField, Button, Grid,
  FormControl, InputLabel, Select, MenuItem, OutlinedInput,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";

import {
  createStrategicIssue,
  fetchValueChains,
  fetchBusinessProcesses,
  fetchStrategicGoals,
  fetchCustomerValues,
  fetchFinancialGoals,
  fetchStrategicIssues,
} from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import { useAuth } from "../../providers/AuthProvider";

export default function SmStrategicIssueCreatePage() {
  const { businessDomainId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const { companyId } = useAuth();
  const t = (id) => intl.formatMessage({ id });

  const [successFactorName, setSuccessFactorName] = useState("");
  const [successFactorDescription, setSuccessFactorDescription] = useState("");
  const [selectedBPIds, setSelectedBPIds] = useState([]);
  const [selectedNames, setSelectedNames] = useState([]);
  const [sourceIds, setSourceIds] = useState([]);
  const [valueChains, setValueChains] = useState([]);
  const [childBusinessProcesses, setChildBusinessProcesses] = useState([]);
  const [businessProcesses, setBusinessProcesses] = useState([]); // combined pool for BP selection
  const [financialGoals, setFinancialGoals] = useState([]);
  const [strategicGoals, setStrategicGoals] = useState([]);
  const [customerValues, setCustomerValues] = useState([]);
  const [strategicIssues, setStrategicIssues] = useState([]);
  const [usedIndicatorNames, setUsedIndicatorNames] = useState(new Set());

  const selectedBPs = selectedBPIds.map((id) => businessProcesses.find((bp) => bp.businessProcessId === id)).filter(Boolean);

  // 親バリューチェーンマップ: 子BPのID → 親バリューチェーン名
  const parentVcMap = useMemo(() => {
    const map = {};
    valueChains.forEach((vc) => {
      (vc.associatedBusinessProcesses || []).forEach((bp) => {
        map[bp.businessProcessId] = vc.businessProcessName;
      });
    });
    return map;
  }, [valueChains]);

  // 指標は選択されたBP（バリューチェーン＋子BP）の indicators を集約
  const sourceIndicators = useMemo(() => {
    const all = selectedBPs.flatMap((bp) => {
      const parentVcName = parentVcMap[bp.businessProcessId];
      return (bp.indicators || []).map((ind) => ({
        ...ind,
        _bpName: bp.businessProcessName,
        _parentVcName: parentVcName || null,
      }));
    });
    const seen = new Set();
    return all.filter((ind) => {
      if (seen.has(ind.indicatorName)) return false;
      seen.add(ind.indicatorName);
      return true;
    });
  }, [selectedBPs, parentVcMap]);

  useEffect(() => {
    (async () => {
      try {
        const [bps, childBps, fgoals, sgoals, cvals, existingSfs] = await Promise.all([
          fetchValueChains(businessDomainId).catch(() => []),
          fetchBusinessProcesses(businessDomainId).catch(() => []),
          fetchFinancialGoals(businessDomainId).catch(() => []),
          fetchStrategicGoals(businessDomainId).catch(() => []),
          fetchCustomerValues(businessDomainId).catch(() => []),
          fetchStrategicIssues(businessDomainId).catch(() => []),
        ]);
        const vcArray = Array.isArray(bps) ? bps : [];
        const childArray = Array.isArray(childBps) ? childBps : [];
        setValueChains(vcArray);
        setChildBusinessProcesses(childArray);
        setBusinessProcesses([...vcArray, ...childArray]);
        setFinancialGoals(Array.isArray(fgoals) ? fgoals : []);
        setStrategicGoals(Array.isArray(sgoals) ? sgoals : []);
        setCustomerValues(Array.isArray(cvals) ? cvals : []);
        const sfArray = Array.isArray(existingSfs) ? existingSfs : [];
        setStrategicIssues(sfArray);
        const used = new Set(
          sfArray
            .flatMap((sf) => sf.indicators || [])
            .map((ind) => ind.indicatorName)
            .filter(Boolean)
        );
        setUsedIndicatorNames(used);
      } catch (e) {
        notify.apiError(e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessDomainId]);

  const handleBPChange = (e) => {
    setSelectedBPIds(e.target.value);
    setSelectedNames([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (sourceIndicators.length > 0 && selectedNames.length === 0) {
      notify.error(t("financialGoal.indicatorRequired"));
      return;
    }
    try {
      const indicators = selectedNames
        .map((name) => sourceIndicators.find((ind) => ind.indicatorName === name))
        .filter(Boolean);
      const sources = sourceIds.map((id) => allSources.find((s) => s.successFactorId === id)).filter(Boolean);
      const bpsToSend = selectedBPs.map((bp) => ({
        businessProcessId: bp.businessProcessId,
        businessProcessName: bp.businessProcessName,
      }));
      await createStrategicIssue({
        successFactorName,
        successFactorDescription,
        businessProcesses: bpsToSend,
        indicators,
        sources,
        businessDomainId,
        companyId,
      });
      notify.success(t("message.create.success"));
      navigate(`/design/${businessDomainId}/strategy-map/strategic-issues`, { replace: true });
    } catch (err) {
      notify.apiError(err);
    }
  };

  const allSources = [...financialGoals, ...customerValues, ...strategicGoals, ...strategicIssues];

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {t("strategicIssue.create.title")}
      </Typography>

      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField
              fullWidth required
              label={t("successFactor.name")}
              value={successFactorName}
              onChange={(e) => setSuccessFactorName(e.target.value)}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth multiline rows={3}
              label={t("successFactor.description")}
              value={successFactorDescription}
              onChange={(e) => setSuccessFactorDescription(e.target.value)}
            />
          </Grid>

          <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel>{t("strategicIssue.businessProcess.label")}</InputLabel>
              <Select
                multiple
                value={selectedBPIds}
                onChange={handleBPChange}
                input={<OutlinedInput label={t("strategicIssue.businessProcess.label")} />}
              >
                {valueChains.length > 0 && [
                  <MenuItem key="__vc_hd" disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary" }}>── {t("valueChain.list.title")} ──</MenuItem>,
                  ...valueChains.map((bp) => (
                    <MenuItem key={bp.businessProcessId} value={bp.businessProcessId}>
                      {bp.businessProcessName}
                    </MenuItem>
                  )),
                ]}
                {childBusinessProcesses.length > 0 && [
                  <MenuItem key="__bp_hd" disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary" }}>── {t("businessProcess.list.title")} ──</MenuItem>,
                  ...childBusinessProcesses.map((bp) => {
                    const vcName = parentVcMap[bp.businessProcessId];
                    return (
                      <MenuItem key={bp.businessProcessId} value={bp.businessProcessId}>
                        {vcName ? `${vcName} > ${bp.businessProcessName}` : bp.businessProcessName}
                      </MenuItem>
                    );
                  }),
                ]}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel>{t("strategicIssue.sources.label")}</InputLabel>
              <Select
                multiple
                value={sourceIds}
                onChange={(e) => setSourceIds(e.target.value)}
                input={<OutlinedInput label={t("strategicIssue.sources.label")} />}
              >
                {financialGoals.length > 0 && [
                  <MenuItem key="__fg_hd" disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary" }}>── {t("financialGoal.list.title")} ──</MenuItem>,
                  ...financialGoals.map((item) => (
                    <MenuItem key={item.successFactorId} value={item.successFactorId}>{item.successFactorName}</MenuItem>
                  )),
                ]}
                {customerValues.length > 0 && [
                  <MenuItem key="__cv_hd" disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary" }}>── {t("customerValue.list.title")} ──</MenuItem>,
                  ...customerValues.map((item) => (
                    <MenuItem key={item.successFactorId} value={item.successFactorId}>{item.successFactorName}</MenuItem>
                  )),
                ]}
                {strategicGoals.length > 0 && [
                  <MenuItem key="__sg_hd" disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary" }}>── {t("strategicGoal.list.title")} ──</MenuItem>,
                  ...strategicGoals.map((item) => (
                    <MenuItem key={item.successFactorId} value={item.successFactorId}>{item.successFactorName}</MenuItem>
                  )),
                ]}
                {strategicIssues.length > 0 && [
                  <MenuItem key="__si_hd" disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary" }}>── {t("strategicIssue.list.title")} ──</MenuItem>,
                  ...strategicIssues.map((item) => (
                    <MenuItem key={item.successFactorId} value={item.successFactorId}>{item.successFactorName}</MenuItem>
                  )),
                ]}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel>{t("financialGoal.selectIndicatorHint")}</InputLabel>
              <Select
                multiple
                disabled={sourceIndicators.length === 0}
                value={selectedNames}
                onChange={(e) => setSelectedNames(e.target.value)}
                input={<OutlinedInput label={t("financialGoal.selectIndicatorHint")} />}
              >
                {sourceIndicators.map((ind) => {
                  const prefix = ind._parentVcName
                    ? `[${ind._parentVcName} > ${ind._bpName}] `
                    : `[${ind._bpName}] `;
                  const isUsed = usedIndicatorNames.has(ind.indicatorName);
                  return (
                    <MenuItem key={ind.indicatorName} value={ind.indicatorName} disabled={isUsed}>
                      {prefix}{ind.indicatorName}（{ind.unit}）{isUsed ? " （登録済み）" : ""}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit" disabled={!successFactorName.trim()}>
            {t("common.add")}
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate(`/design/${businessDomainId}/strategy-map/strategic-issues`)}
          >
            {t("common.cancel")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
